import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { OrdersApiService } from '../../../services/orders-api.service';
import { Order } from '../../../models/order';
import { LimitOffsetPagination } from '../../../../../shared/models/limit-offset-pagination';
import { TablePaginationComponent } from "../../../../../shared/components/table-pagination/table-pagination.component";
import { TableSkeletonRowComponent } from "../../../../../shared/components/table-skeleton-row/table-skeleton-row.component";
import { ToastNotificationService } from '../../../../../notifications/services/toast-notification.service';
import { ToastNotificationType } from '../../../../../notifications/models/toast-notification-type';
import { FilteringChipsComponent } from "../../../../../shared/components/filtering-chips/filtering-chips.component";
import { GetOrdersResponse } from '../../../models/get-orders-response';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { FiltersDropdownComponent, FilterDef, FilterValues } from '../../../../../shared/filters-dropdown';
import { ExportExcelButtonComponent } from '../../../../../shared/components/export-excel-button/export-excel-button.component';
import { OverlayModule } from "@angular/cdk/overlay";
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../../../../shared/services/translations/language.service';

@Component({
  selector: 'app-orders-page',
  imports: [CommonModule, FormsModule, RouterLink, TablePaginationComponent, FilteringChipsComponent, TableSkeletonRowComponent, FiltersDropdownComponent, ExportExcelButtonComponent, OverlayModule, TranslateModule],
  templateUrl: './orders-page.component.html',
  styleUrl: './orders-page.component.css'
})
export class OrdersPageComponent implements OnInit {

  apiService = inject(OrdersApiService);
  ordersResponse: GetOrdersResponse | undefined;
  pagination: LimitOffsetPagination = { limit: 10, offset: 0 };
  toastNotificationService = inject(ToastNotificationService);
  translate = inject(TranslateService);
  languageService = inject(LanguageService);

  statusFilters: { name: string, value: string }[] = [
    { name: 'orders.status_filters.all', value: '' },
    { name: 'orders.status_filters.placed', value: 'placed' },
    { name: 'orders.status_filters.preparing_order', value: 'preparing_order' },
    { name: 'orders.status_filters.ready_for_pickup', value: 'ready_for_pickup' },
    { name: 'orders.status_filters.out_for_delivery', value: 'out_for_delivery' },
    { name: 'orders.status_filters.delivered', value: 'delivered' },
    { name: 'orders.status_filters.cancelled', value: 'cancelled' },
    { name: 'orders.status_filters.refunded', value: 'refunded' }
  ];

  search = '';
  searchSubject = new Subject<string>(); // for debounce

  selectedStatus = '';
  filterValues: FilterValues = {};

  orderFilters: FilterDef[] = [];

  ngOnInit(): void {
    this.orderFilters = this.buildFilters();
    this.getOrders();

    // setup debounced search
    this.searchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged(),
    ).subscribe(searchText => {
      this.search = searchText;
      this.pagination = { limit: 10, offset: 0 };
      this.getOrders();
    });

    this.translate.onLangChange.subscribe(() => {
      this.orderFilters = this.buildFilters();
      this.getOrders();
    });
  }

  private buildFilters(): FilterDef[] {
    const currency = this.translate.instant('common.currency');
    return [
      {
        kind: 'numberRange',
        key: 'price',
        label: `${this.translate.instant('orders.filters.total')} (${currency})`,
        minPlaceholder: this.translate.instant('orders.filters.from'),
        maxPlaceholder: this.translate.instant('orders.filters.to'),
      },
      {
        kind: 'dateRange',
        key: 'placedAt',
        label: this.translate.instant('orders.filters.placed_at'),
        placeholder: this.translate.instant('orders.filters.date_placeholder'),
      },
    ];
  }

  getOrders(status?: string) {
    if (status !== undefined) this.selectedStatus = status;
    this.ordersResponse = undefined;

    this.apiService.getOrders(this.pagination, this.selectedStatus, this.search, this.toApiFilters()).subscribe({
      next: orders => {
        this.ordersResponse = orders;
      },
      error: error => {
        this.toastNotificationService.createNotification(this.translate.instant('orders.errors.fetch_failed'), ToastNotificationType.ERROR);
      }
    });
  }

  onFiltersChange(values: FilterValues) {
    this.filterValues = values;
    this.pagination = { limit: this.pagination.limit, offset: 0 };
    this.getOrders();
  }

  private toApiFilters(): Record<string, string | number> {
    const out: Record<string, string | number> = {};
    const price = this.filterValues['price'];
    if (price?.min != null) out['min_total'] = price.min;
    if (price?.max != null) out['max_total'] = price.max;
    const range = this.filterValues['placedAt'];
    if (range?.start) out['placed_from'] = range.start;
    if (range?.end) out['placed_to'] = range.end;
    return out;
  }

  updatePagination(pagination: LimitOffsetPagination) {
    this.pagination = pagination;
    this.getOrders();
  }

  onSearchChange(event: any) {
    const value = event.target.value.trim().replace(/^#/, '');
    this.searchSubject.next(value);
  }

  isExporting = false;

  onExportExcel(): void {
    if (this.isExporting) return;
    this.isExporting = true;
    this.apiService.exportOrders(this.selectedStatus, this.search, this.toApiFilters()).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `orders-${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        this.isExporting = false;
      },
      error: () => {
        this.toastNotificationService.createNotification(
          this.translate.instant('orders.errors.export_failed'),
          ToastNotificationType.ERROR,
        );
        this.isExporting = false;
      },
    });
  }

}
